import React, { useEffect, useState } from 'react';
import { Button, Page, Spinner, Stack } from '@shopify/polaris';
import { gql, useApolloClient, useMutation } from '@apollo/client';
import DeliveryDate from './DeliveryDate';
import ManufacturerGroup from './ManufacturerGroup';
import { Manufacturer, META_STATUS_KEY } from '../../utils/types';
import {
  getActualOrderStatus,
  getStatusNotes,
  isSentToAllManufacturers,
} from '../../utils/order/orderStatus';
import { groupItemsByManufacturer } from '../../utils/order/orderItems';
import useSendOrderToCustomer from '../../utils/sendEmail/sendToCustomer';
import { useSendItemsToManufacturer } from '../../utils/sendEmail/sendToManufacturer';
import {
  updProductMutation,
  updProductOpts,
} from '../../queries/shopify/updProduct';
import { NoOrderData } from '../Helpers';
import { ManufacturerStatus, OrderStatus } from '../../models/orderStatus';
import { SendStatus } from '../../models/sendStatus';
import type { Item } from '../../models/item';
import { Order } from '../../models/order';
import { ShipDate } from '../../models/shipDate';
import { isMetafieldOrderStatus } from '../../utils/order/orderData';

type ContentProps = {
  order: Order;
  saveStatusHandler: (status: Partial<OrderStatus>, manufacturers?: Manufacturer[]) => Promise<void> | void;
  saveShipDateHandler: (date: ShipDate) => Promise<void> | void;
  setVendor: (productId: string, vendor: Manufacturer) => Promise<void> | void;
};

const Content = ({
  order,
  saveStatusHandler,
  saveShipDateHandler,
  setVendor,
}: ContentProps) => {
  const sendItemsToManufacturer = useSendItemsToManufacturer();
  const sendOrderToCustomer = useSendOrderToCustomer();
  const groupedItems = groupItemsByManufacturer(order.items);

  const groupedStatus = getActualOrderStatus(
    isMetafieldOrderStatus(order.sendManStatus)
      ? order.sendManStatus.value
      : order.sendManStatus,
    groupedItems,
  );

  const [groupedNotes, setGroupedNotes] = useState(
    getStatusNotes(groupedStatus),
  );
  const [shouldSendToAll, setShouldSendToAll] = useState(false);

  useEffect(() => {
    if (shouldSendToAll) sendToAll();
  }, [shouldSendToAll]);

  function manufacturers() {
    return Object.keys(groupedItems) as Manufacturer[];
  }

  async function sendToAll() {
    const newStatus: OrderStatus = {};
    await Promise.all(
      manufacturers().map(async man => {
        newStatus[man] = await sendItemsToManHandler(man);
      }),
    );
    await saveStatusHandler(newStatus, manufacturers());
    setShouldSendToAll(false);
  }

  async function sendToManufacturer(man: Manufacturer) {
    await saveStatusHandler(
      {
        ...groupedStatus,
        [man]: await sendItemsToManHandler(man),
      },
      manufacturers(),
    );
  }

  const [loading, setLoading] = useState(false);
  const changeItemManufacturer = (newManufacturer: Manufacturer, item: Item) => {
    setVendor(item.id, newManufacturer);
  };

  async function sendItemsToManHandler(manufacturer: Manufacturer) {
    const items = groupedItems[manufacturer];
    const man = await sendItemsToManufacturer({
      orderName: order.name,
      manufacturer,
      items,
      notes: groupedNotes[manufacturer] || {},
    });
    return man;
  }

  function updateManNotesHandler(manufacturer: Manufacturer, notes: ManufacturerStatus['notes']) {
    setGroupedNotes({
      ...groupedNotes,
      [manufacturer]: notes,
    });
  }

  return (
    <Page
      title={`Order ${order.name}`}
      primaryAction={
        <Stack alignment="center">
          <Stack.Item>
            <DeliveryDate
              shipDateObject={order.sendManShipDate?.value}
              allowSend={isSentToAllManufacturers(
                groupedStatus,
              )}
              saveDateHandler={saveShipDateHandler}
              sendCustomerEmailHandler={sendOrderToCustomer({
                orderName: order.name,
                items: order.items,
                total: order.totalPrice,
                note: order.note,
                customerEmail: order.customer.email,
                customerName: order.customer.firstName,
              })}
              emailConfirmed={!!order.customer.email.length}
            />
          </Stack.Item>
          <Stack.Item>
            <Button
              primary
              disabled={
                shouldSendToAll ||
                !!manufacturers().find(
                  man =>
                    groupedStatus[man]?.status === SendStatus.sent,
                )
              }
              onClick={() => setShouldSendToAll(true)}
            >
              {shouldSendToAll ? <Spinner size="small" /> : 'Send to all'}
            </Button>
          </Stack.Item>
        </Stack>
      }
    >
      {loading ? (
        <NoOrderData loading title="Loading group..." />
      ) : (
        manufacturers().map(man => (
          <ManufacturerGroup
            key={man}
            manufacturer={man}
            orderName={order.name}
            items={groupedItems[man]}
            statusObject={groupedStatus[man]}
            updateNotesHandler={notes => updateManNotesHandler(man, notes)}
            sendEmailHandler={async () => {
              await sendToManufacturer(man);
            }}
            sendingAll={shouldSendToAll}
            changeItemManufacturer={changeItemManufacturer}
          />
        ))
      )}
    </Page>
  );
};

export default Content;
